import { useEffect, useRef, useState } from "react";
import { X, Send, Sparkles } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";
import SleepyBear from "@/components/SleepyBear";

const STORAGE_KEY = "mkb-nextbook-popup";
const SHOW_DELAY_MS = 8000; // 8s after mount

/**
 * A gentle, once-per-visitor slide-up popup on the Home page inviting
 * readers to subscribe for the next book announcement.
 *
 * Dismissal is remembered in localStorage — subscribing or clicking the
 * close button prevents the popup from ever reappearing for that browser.
 */
export default function NextBookPopup() {
  const [open, setOpen] = useState(false);
  const [email, setEmail] = useState("");
  const [busy, setBusy] = useState(false);
  const timer = useRef(null);

  useEffect(() => {
    if (typeof window === "undefined") return;
    try {
      if (localStorage.getItem(STORAGE_KEY) === "dismissed") return;
    } catch { /* ignore */ }
    timer.current = setTimeout(() => setOpen(true), SHOW_DELAY_MS);
    return () => timer.current && clearTimeout(timer.current);
  }, []);

  const dismiss = () => {
    setOpen(false);
    try { localStorage.setItem(STORAGE_KEY, "dismissed"); } catch { /* ignore */ }
  };

  const subscribe = async (e) => {
    e.preventDefault();
    if (!email) return;
    setBusy(true);
    try {
      await api.post("/newsletter/subscribe", { email });
      toast.success("You're on the list! Check your inbox for a hello. 💌");
      dismiss();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Could not subscribe");
    } finally {
      setBusy(false);
    }
  };

  if (!open) return null;

  return (
    <div
      className="fixed z-50 max-w-sm w-[calc(100%-2rem)] sm:w-96"
      style={{
        left: "1rem",
        bottom: "1rem",
        animation: "mkbPopupIn 500ms cubic-bezier(0.22,1,0.36,1) both",
      }}
      role="dialog"
      aria-live="polite"
      data-testid="nextbook-popup"
    >
      <style>{`
        @keyframes mkbPopupIn {
          from { transform: translateY(24px); opacity: 0; }
          to   { transform: translateY(0);    opacity: 1; }
        }
      `}</style>
      <div
        className="relative rounded-3xl p-5 sm:p-6"
        style={{
          background: "linear-gradient(135deg, #FFFFFF 0%, #FFF3F8 100%)",
          border: "1px solid #F0EBFF",
          boxShadow: "0 24px 60px rgba(45,48,85,0.18)",
        }}
      >
        <button
          type="button"
          onClick={dismiss}
          className="absolute top-3 right-3 p-1.5 rounded-full transition-colors hover:bg-[#FFE6F0]"
          aria-label="Close notification"
          data-testid="nextbook-popup-close"
        >
          <X size={16} style={{ color: "#7E82A1" }} />
        </button>

        <div className="flex items-start gap-3">
          <span
            className="w-12 h-12 rounded-2xl flex items-center justify-center shrink-0 overflow-hidden"
            style={{
              background: "radial-gradient(circle at 30% 30%, #2A3763 0%, #0E1A38 100%)",
              boxShadow: "0 10px 22px rgba(14,26,56,0.32)",
              position: "relative",
            }}
          >
            {/* tiny crescent moon behind the bear */}
            <span
              aria-hidden="true"
              style={{
                position: "absolute",
                top: 4,
                left: 5,
                width: 10,
                height: 10,
                borderRadius: "50%",
                background: "#FFE9A3",
                boxShadow: "inset -3px 0 0 0 #2A3763, 0 0 8px rgba(255,233,163,0.7)",
              }}
            />
            <SleepyBear size={40} />
          </span>
          <div className="min-w-0 pr-6">
            <span className="font-caveat text-lg leading-none" style={{ color: "#FF6B9D" }}>
              hot off the writing desk
            </span>
            <h3
              className="font-fredoka text-xl leading-tight mt-1"
              style={{ color: "#2D3055" }}
              data-testid="nextbook-popup-title"
            >
              Notify me when the next book drops
            </h3>
            <p className="mt-1.5 text-sm font-nunito" style={{ color: "#4A4E6D" }}>
              A single, gentle note the day it releases — no spam, ever.
            </p>
          </div>
        </div>

        <form onSubmit={subscribe} className="mt-4 flex gap-2" data-testid="nextbook-popup-form">
          <input
            type="email"
            required
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="you@lovely.com"
            className="flex-1 px-4 py-2.5 rounded-full text-sm outline-none border font-nunito"
            style={{ borderColor: "#F0D9E8", background: "#FFFFFF", color: "#2D3055" }}
            data-testid="nextbook-popup-email"
          />
          <button
            type="submit"
            disabled={busy}
            className="px-4 py-2.5 rounded-full inline-flex items-center gap-1.5 font-fredoka text-sm transition-transform hover:-translate-y-0.5 disabled:opacity-60 disabled:hover:translate-y-0"
            style={{ background: "#FF6B9D", color: "#FFFFFF", boxShadow: "0 8px 18px rgba(255,107,157,0.28)" }}
            data-testid="nextbook-popup-submit"
          >
            {busy ? "…" : (<><Send size={14} /> Notify me</>)}
          </button>
        </form>

        <p className="mt-3 flex items-center gap-1.5 text-xs font-nunito" style={{ color: "#7E82A1" }}>
          <Sparkles size={12} style={{ color: "#FFD93D" }} /> ~2 emails a year. Unsubscribe any time.
        </p>
      </div>
    </div>
  );
}
